// components/about/CreativeUsesSection.js
import React, { useRef, useState, useEffect } from 'react';

const CreativeUsesSection = ({ videoSrc }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  // Set up intersection observer to detect when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // Handle video loaded event
  const handleVideoLoaded = () => {
    setIsVideoLoaded(true);
  };

  const handleVideoError = () => {
    setHasVideoError(true);
  };

  // Handle video playback
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!isVisible || !videoEl) return;

    const playPromise = videoEl.play();
    
    // Handle promise to avoid uncaught error when autoplay is blocked
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.warn('Video autoplay was prevented:', error);
      });
    }
    
    return () => {
      videoEl.pause();
    };
  }, [isVisible]);

  return (
    <section 
      ref={sectionRef}
      className="py-16 relative overflow-hidden"
    >
      {/* Video Background - Only load when in view */}
      <div className="absolute inset-0">
        {/* Loading placeholder */}
        {(!isVisible || !isVideoLoaded) && !hasVideoError && (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="w-16 h-16 border-4 border-t-red-600 border-r-transparent border-b-red-600 border-l-transparent rounded-full animate-spin"></div>
          </div>
        )}
        
        {/* Only render video when section is in view */}
        {isVisible && (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`w-full h-full object-cover transition-opacity duration-700 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoadedData={handleVideoLoaded}
            onError={handleVideoError}
            preload="metadata"
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
        <div className="absolute inset-0 bg-black opacity-70"></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Creative Uses for 3D Printing
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-red-700 to-red-500 rounded mx-auto"></div>
        </div>

        <div className="bg-gray-900 bg-opacity-25 backdrop-blur-sm p-8 rounded-xl">
          <p className="text-lg text-gray-300 mb-6">
            Maybe you know exactly what you want to use a 3D printer for, but
            we have plenty of ideas if you're looking for inspiration, too.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Personal Items
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span className="text-gray-300">
                    Design a completely unique necklace for yourself or a friend
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span className="text-gray-300">
                    Make a 3D print of a favorite selfie, saving a special moment forever
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Home & Garden
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span className="text-gray-300">
                    Create plant pots with custom designs
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span className="text-gray-300">
                    Make coasters or trays with personalized patterns
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span className="text-gray-300">
                    Design a unique sculpture or piece of art for your home or garden
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-lg text-gray-300 mt-8 text-center font-semibold">
            "If you can dream it, we will build it."
          </p>
        </div>
      </div>
    </section>
  );
};

export default CreativeUsesSection;